import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Calendar, Users, ListChecks, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { LoaderBlock, EmptyState } from '../components/listHelpers';
import { StatusBadge } from '../components/submitHelpers';

function relativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SubmissionCard({ kind, item, icon: Icon }) {
  const title = kind === 'event' ? item.title : item.name;
  const meta = kind === 'event'
    ? `${item.category} · ${item.venue || ''}, ${item.city}`
    : `${item.category} · ${item.city}`;
  const img = item.images?.[0] || (kind === 'event' ? item.image_url : null);

  return (
    <li
      data-testid={`my-submission-${item.id}`}
      className="flex gap-3 p-4 bg-white border border-[#F4F1DE] rounded-2xl"
    >
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#FFE5DD] flex-shrink-0 flex items-center justify-center">
        {img ? <img src={img} alt={title} className="w-full h-full object-cover" /> : <Icon className="w-6 h-6 text-[#C8553D]/40" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-[#2B2D42] truncate">{title}</h3>
            <p className="text-xs text-[#6D6875] truncate">{meta}</p>
          </div>
          <StatusBadge status={item.status} />
        </div>
        <div className="flex items-center justify-between mt-2 text-[10px] text-[#B5B5B5]">
          <span>Submitted {relativeDate(item.created_at)}</span>
          {item.reviewed_at && <span>Reviewed {relativeDate(item.reviewed_at)}</span>}
        </div>
        {item.admin_notes && (
          <div className="mt-2 p-2 bg-[#FFE0DA]/50 rounded text-xs text-[#6D6875]">
            <span className="font-bold text-[#C8553D]">Admin note: </span>{item.admin_notes}
          </div>
        )}
      </div>
    </li>
  );
}

export default function MySubmissions() {
  const [data, setData] = useState({ business_submissions: [], event_submissions: [], community_submissions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api('/submissions/my');
        setData(res);
      } catch (err) {
        toast.error(err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = data.business_submissions.length + data.event_submissions.length + data.community_submissions.length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="my-submissions-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
            My Submissions
          </h1>
          <p className="text-sm text-[#6D6875] mt-1">Track approval status of your listings</p>
        </div>
        <Link
          to="/app/submit"
          data-testid="my-submissions-new-button"
          className="inline-flex items-center gap-2 bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-4 py-2 rounded-full text-sm"
        >
          <Plus className="w-4 h-4" /> New
        </Link>
      </div>

      {loading ? (
        <LoaderBlock testId="my-submissions-loading" />
      ) : total === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="You haven't submitted anything yet"
          sub="Help grow the directory — submit a business, event, or community."
          action={
            <Link
              to="/app/submit"
              data-testid="my-submissions-empty-cta"
              className="mt-4 inline-flex bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-5 py-2.5 rounded-full text-sm"
            >
              Submit a Listing
            </Link>
          }
          testid="my-submissions-empty"
        />
      ) : (
        <div className="space-y-6">
          {data.business_submissions.length > 0 && (
            <section data-testid="my-submissions-businesses">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Businesses ({data.business_submissions.length})
              </h2>
              <ul className="space-y-3">
                {data.business_submissions.map((s) => (
                  <SubmissionCard key={s.id} kind="business" item={s} icon={Building2} />
                ))}
              </ul>
            </section>
          )}

          {data.event_submissions.length > 0 && (
            <section data-testid="my-submissions-events">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Events ({data.event_submissions.length})
              </h2>
              <ul className="space-y-3">
                {data.event_submissions.map((s) => (
                  <SubmissionCard key={s.id} kind="event" item={s} icon={Calendar} />
                ))}
              </ul>
            </section>
          )}

          {data.community_submissions.length > 0 && (
            <section data-testid="my-submissions-communities">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" /> Communities ({data.community_submissions.length})
              </h2>
              <ul className="space-y-3">
                {data.community_submissions.map((s) => (
                  <SubmissionCard key={s.id} kind="community" item={s} icon={Users} />
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
